import React from 'react';
import { Switch, Flex, Space, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import ButtonWithRole from '../../../components/ButtonWithRole';
import { CONTENT_WRITE_REFUSED_ROLES } from '../../auth/roleCapabilities';
import { NewClearCacheCard } from '../../newClearCache/NewClearCacheCard';
import CacheExpirationDropdownLegacy from './CacheExpiration/CacheExpirationDropdownLegacy';
import CacheExpirationDropdown from './CacheExpiration/CacheExpirationDropdown';
import UpgradePlanProvider from '../../UpgradePlan/UpgradePlanProvider';
import useFeatureFlag, { FeatureFlag } from '../../../hooks/useFeatureFlag';
import { CustomTooltip } from '../../../components/Tooltip';

// The count comes from the `user:{id}:num_cached` Redis counter, while an export
// runs a live query against the `cached_urls` replica and also carries the pages
// still being processed. The two drift apart by design, so the heading states an
// approximate figure and the icon explains the gap.
const COUNT_APPROXIMATE_TOOLTIP =
  'This number may differ slightly from the URLs included in your export file because the export uses the latest data with URLs that are still being processed.';

type HeadingProps = {
  cacheSettingsToolTip: string;
  domainsAmount: number;
  cachedPagesAmount: number;
  query?: string;
  lastCrawledFilterActive: boolean;
  setLastCrawledFilterActive: (value: boolean) => void;
  onAddUrlClick: () => void;
  onUpdateCacheFreshness: (days: number) => void;
};

function Heading({
  cacheSettingsToolTip,
  domainsAmount,
  cachedPagesAmount,
  query,
  lastCrawledFilterActive,
  setLastCrawledFilterActive,
  onAddUrlClick,
  onUpdateCacheFreshness,
}: HeadingProps) {
  const isNewPricing2025 = useFeatureFlag([FeatureFlag.Pricing2025]);
  const { token } = theme.useToken();

  return (
    <Flex align="flex-start" justify="space-between">
      <Space direction="vertical" style={{ marginBottom: '16px' }}>
        <div>
          <Flex align="center" gap={token.marginXXS}>
            <span>
              {`You have ~${cachedPagesAmount.toLocaleString('en-US')} cached URLs`}
              {domainsAmount > 1 && ` across ${domainsAmount.toLocaleString('en-US')} domains`}
            </span>
            <CustomTooltip placement="top" message={COUNT_APPROXIMATE_TOOLTIP}>
              <InfoCircleOutlined style={{ color: token.colorIcon }} />
            </CustomTooltip>
          </Flex>

          <p>{query && <b className="ml-2">{query}</b>}</p>
        </div>

        <Flex gap="small">
          <Switch title="Not crawled pages" value={lastCrawledFilterActive} onChange={setLastCrawledFilterActive} />
          <Typography.Text style={{ fontSize: token.fontSizeSM }} strong>
            Pages that have not been requested in the past 30 days
          </Typography.Text>
        </Flex>
      </Space>
      <Flex gap="small">
        {isNewPricing2025 ? (
          <UpgradePlanProvider>
            <CacheExpirationDropdown onUpdateCacheFreshness={onUpdateCacheFreshness} />
          </UpgradePlanProvider>
        ) : (
          <CacheExpirationDropdownLegacy
            onUpdateCacheFreshness={onUpdateCacheFreshness}
            cacheSettingsToolTip={cacheSettingsToolTip}
          />
        )}

        <ButtonWithRole disabledFor={CONTENT_WRITE_REFUSED_ROLES} onClick={onAddUrlClick}>
          Add URLs
        </ButtonWithRole>
        <NewClearCacheCard />
      </Flex>
    </Flex>
  );
}

export default Heading;
